import React, { ReactNode, useState } from "react";
import { ArrowDownward, ArrowUpward, DeleteForever } from "@mui/icons-material";
import { AppBar, Button, ButtonGroup, Checkbox, Divider, IconButton, Stack, Toolbar, Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ColumnModelItem } from "components/tables/columns/ColumnModel";

interface ColumnToolbarProps {
  // Array of columns currently selected
  activeColumns: ColumnModelItem[];
  // Are all columns active?
  areAllActive: boolean;
  children: ReactNode;
  /**
   * Handles moving a single column down
   * @param colIndex: current index of column to move down
   */
  moveColumnDown: (colIndex: number) => void;
  /**
   * Handles moving a single column up
   * @param colIndex: current index of column to move up
   */
  moveColumnUp: (colIndex: number) => void;
  // Handles deleting/hiding multiple columns
  onRemoveMultipleColumns: (activeColumns: ColumnModelItem[]) => void;
  // Toggles the active state of all columns
  toggleAllColumns: (toggleOption?: "none") => void;
  // Array of objects representing the visible column's properties.
  visibleColumns: ColumnModelItem[];
}

export default function ColumnToolbar(props: ColumnToolbarProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const indeterminate = (props.visibleColumns.length !== props.activeColumns.length) && props.activeColumns.length > 0;

  return (
    <AppBar
      position="sticky"
      sx={{ background: theme.palette.background.default, padding: 1 }}
    >
      {props.children}
      <Divider sx={{ mt: 1, mb: 1, width: "100%" }} />
      <Toolbar>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%"
          }}>
          <Stack direction="row" sx={{
            gap: 1
          }}>
            {/* Set all columns active */}
            <Tooltip title={props.areAllActive ? i18n.t("tables.deselect-all") : i18n.t("tables.select-all")}>
              <Checkbox
                checked={props.visibleColumns.length > 0 ? props.areAllActive : isChecked}
                indeterminate={indeterminate}
                onChange={() => {
                  if (props.visibleColumns.length > 0) {
                    props.toggleAllColumns();
                  } else {
                    setIsChecked(prev => !prev);
                  }
                }}
              />
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            {/* Move up & down buttons */}
            <ButtonGroup
              disabled={props.activeColumns.length !== 1}
              size="small"
              sx={{
                "&.MuiButtonGroup-root > span > button > span": { margin: 0 },
                background: theme.colors.primary.lighter,
                opacity: props.activeColumns.length > 0 ? 1 : 0.4
              }}>
              {/* Move Column Up */}
              <Tooltip title={i18n.t("tables.move-selected-column-up")}>
                {/* Span is here because a warning is thrown if tooltip wraps something not always visible */}
                <span>
                  <Button
                    disabled={props.activeColumns[0] === props.visibleColumns[0]}
                    onClick={() => {
                      const foundIndex = props.visibleColumns.findIndex(item => {
                        return item.field === props.activeColumns[0].field;
                      });
                      props.moveColumnUp(foundIndex);
                    }}
                    startIcon={<ArrowUpward />}
                    sx={{ background: "transparent", height: "100%" }}
                  />
                </span>
              </Tooltip>
              {/* Move Column Down */}
              <Tooltip title={i18n.t("tables.move-selected-column-down")}>
                <span>
                  <Button
                    disabled={
                      props.activeColumns[0] ===
                      props.visibleColumns[props.visibleColumns.length - 1]
                    }
                    onClick={() => {
                      const foundIndex = props.visibleColumns.findIndex(item => {
                        return item.field === props.activeColumns[0].field;
                      });
                      props.moveColumnDown(foundIndex);
                    }}
                    startIcon={<ArrowDownward />}
                    sx={{ background: "transparent", height: "100%" }}
                  />
                </span>
              </Tooltip>
            </ButtonGroup>
            <Divider orientation="vertical" flexItem />
          </Stack>
          {/* Remove Column */}
          <Stack
            direction="row"
            sx={{
              gap: .5,
              opacity: props.activeColumns.length > 0 ? 1 : 0.4
            }}>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={i18n.t("tables.remove-selected-columns")}>
              {/* Wrap button in span so tooltip shows when button is disabled */}
              <span>
                <IconButton
                  disabled={props.activeColumns.length < 1}
                  onClick={() => {
                    props.onRemoveMultipleColumns(props.activeColumns);
                    props.toggleAllColumns("none");
                  }}
                >
                  <DeleteForever sx={{ color: theme.palette.error.main }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
